import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';  // Import axios to send http requests
import { useNavigate, useParams } from 'react-router-dom';  // Import useNavigate and useParams to navigate to different pages
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Box,
  Radio,
  RadioGroup,
  FormLabel,
  FormHelperText
} from '@mui/material';
import { format } from 'date-fns';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';

import { useNotification } from '../contexts/NotificationContext';
import {
  surgeryData,
  surgeonList,
  assistantSurgeonList,
  anesthesiologistList,
  nursingStaffList,
  theatreOptions,
  allergiesOptions,
  insuranceOptions,
  roomNumbers
} from '../utils/dataConstants';

const formatDate = (date) => {
  return format(new Date(date), 'yyyy-MM-dd HH:mm:ss');
};

const EditOtBooking = () => {
  const { showNotification } = useNotification();
  const { uhid_no } = useParams();
  const [allergiesError, setAllergiesError] = React.useState(false);
  const [errors, setErrors] = useState({});
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handleBlur = (event) => {
    const { name, value } = event.target;
    let error = "";
    const req = {
      ip_no: "IP No", uhid_no: "Patient Name", type_of_surgery: "Type of Surgery",
      diagnosis_reason: "Diagnosis Reason", expected_duration: "Expected Duration",
      preferred_operating_theatre: "Preferred Operating Theatre", priority_status: "Priority Status",
      primary_surgeon_name: "Primary Surgeon Name", anesthesiologist_name: "Anesthesiologist Name",
      nursing_staff_names: "Nursing Staff Name", payment_method: "Payment Method",
      room_no: "Room No", surgery_datetime: "Surgery Date & Time", operation_status: "Operation Status"
    };

    if (req[name] && !String(value).trim()) error = `${req[name]} is required`;
    if (name === "surgery_datetime" && value && new Date(value) < new Date()) error = "Surgery date cannot be in the past";
    if (name === "expected_duration" && value && Number(value) <= 0) error = "Duration must be greater than 0";

    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: "",
    patient_name: "",
    booking_datetime: "",
    allergies_medical_alerts: [],
    type_of_surgery: "",
    surgical_code: "",
    procedure_code: "",
    type_of_procedure: "",
    diagnosis_reason: "",
    expected_duration: "",
    surgery_datetime: "",
    preferred_operating_theatre: "",
    priority_status: "",
    primary_surgeon_name: "",
    primary_surgeon_id: "",
    assistant_surgeon_names: "",
    assistant_surgeon_ids: "",
    anesthesiologist_name: "",
    anesthesiologist_id: "",
    nursing_staff_names: "",
    nursing_staff_ids: "",
    preoperative_tests_required: false,
    special_equipment_needed: false,
    blood_products_required: false,
    fasting_instructions_given: false,
    preoperative_medications_provided: false,
    consent_forms_completed: false,
    is_major_ot: false,
    room_no: "",
    insurance_details: "",
    payment_method: "",
    operation_status: "",
    online_payment_option: "",
    card_type: "",
  });
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const allergyInputRef = useRef(null);

  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/get_ot_booking_by_uhid/${uhid_no}`, { withCredentials: true });
        const otBookingData = response.data;
        setFormData({
          ...otBookingData,
          allergies_medical_alerts: Array.isArray(otBookingData.allergies_medical_alerts)
            ? otBookingData.allergies_medical_alerts
            : typeof otBookingData.allergies_medical_alerts === 'string' && otBookingData.allergies_medical_alerts.length > 0
              ? otBookingData.allergies_medical_alerts.split(',').map(s => s.trim())
              : [],
          assistant_surgeon_ids: (typeof otBookingData.assistant_surgeon_ids === 'string' && otBookingData.assistant_surgeon_ids.startsWith('['))
            ? JSON.parse(otBookingData.assistant_surgeon_ids).join(', ') : otBookingData.assistant_surgeon_ids,
          nursing_staff_ids: (typeof otBookingData.nursing_staff_ids === 'string' && otBookingData.nursing_staff_ids.startsWith('['))
            ? JSON.parse(otBookingData.nursing_staff_ids).join(', ') : otBookingData.nursing_staff_ids,
          assistant_surgeon_names: (typeof otBookingData.assistant_surgeon_names === 'string' && otBookingData.assistant_surgeon_names.startsWith('['))
            ? JSON.parse(otBookingData.assistant_surgeon_names).join(', ') : otBookingData.assistant_surgeon_names,
          nursing_staff_names: (typeof otBookingData.nursing_staff_names === 'string' && otBookingData.nursing_staff_names.startsWith('['))
            ? JSON.parse(otBookingData.nursing_staff_names).join(', ') : otBookingData.nursing_staff_names,
          actual_start_time: otBookingData.actual_start_time ? formatDate(otBookingData.actual_start_time) : "",
        });
      } catch (error) {
        console.error('Error fetching patient data:', error);
        showNotification('error', 'Failed to fetch OT booking details.');
      }
    };
    fetchPatientData();
  }, [uhid_no]);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));

    const autoFillMap = {
      primary_surgeon_name: { list: surgeonList, matchField: "name", valueField: "id", idField: "primary_surgeon_id" },
      assistant_surgeon_names: { list: assistantSurgeonList, matchField: "name", valueField: "id", idField: "assistant_surgeon_ids" },
      anesthesiologist_name: { list: anesthesiologistList, matchField: "name", valueField: "id", idField: "anesthesiologist_id" },
      nursing_staff_names: { list: nursingStaffList, matchField: "name", valueField: "id", idField: "nursing_staff_ids" }
    };

    if (name === 'type_of_surgery') {
      const selected = surgeryData.find(s => s.diagnosisDescription === value);
      if (selected) {
        setFormData(prev => ({
          ...prev, type_of_surgery: value, surgical_code: selected.icd10Code, procedure_code: selected.procedureCode, type_of_procedure: selected.procedureDescription
        }));
      } else setFormData(prev => ({ ...prev, [name]: value }));
    } else if (autoFillMap[name]) {
      const cfg = autoFillMap[name];
      const selected = cfg.list.find(item => item[cfg.matchField] === value);
      setFormData(prev => ({ ...prev, [name]: value, [cfg.idField]: selected ? selected[cfg.valueField] : "" }));
    } else if (name === 'insurance_details') {
      setFormData(prev => ({ ...prev, insurance_details: value, payment_method: value ? 'Insurance' : prev.payment_method }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const validateForm = () => {
    const temp = {};
    const req = {
      ip_no: "IP No", uhid_no: "Patient Name", type_of_surgery: "Type of Surgery",
      diagnosis_reason: "Diagnosis Reason", expected_duration: "Expected Duration",
      preferred_operating_theatre: "Preferred Operating Theatre", priority_status: "Priority Status",
      primary_surgeon_name: "Primary Surgeon Name", anesthesiologist_name: "Anesthesiologist Name",
      nursing_staff_names: "Nursing Staff Name", payment_method: "Payment Method",
      room_no: "Room No", surgery_datetime: "Surgery Date & Time", operation_status: "Operation Status"
    };

    for (const field in req) {
      if (!formData[field] || String(formData[field]).trim() === "") {
        temp[field] = `${req[field]} is required`;
      }
    }
    if (formData.expected_duration && Number(formData.expected_duration) <= 0) {
      temp.expected_duration = "Duration must be greater than 0";
    }

    setErrors(temp);
    const hasError = Object.keys(temp).length > 0;
    if (hasError) {
      showNotification('error', `Please fix: ${Object.values(temp)[0]}`);
    }
    return !hasError;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const typed = allergyInputRef.current?.value?.trim();
    let allergies = [...formData.allergies_medical_alerts];
    if (typed && !allergies.includes(typed)) allergies.push(typed);

    if (formData.payment_method === "Online" && !formData.online_payment_option) {
      showNotification('error', "Please select an Online Payment Option.");
      return;
    }
    if (formData.payment_method === "Online" && formData.online_payment_option === "card" && !formData.card_type) {
      showNotification('error', "Please select Card Type.");
      return;
    }

    const formatted = {
      ...formData,
      allergies_medical_alerts: allergies.join(', '),
      surgery_duration: formData.expected_duration,
      surgery_datetime: formatDate(formData.surgery_datetime),
      assistant_surgeon_names: JSON.stringify([String(formData.assistant_surgeon_names || "").trim()].filter(Boolean)),
      nursing_staff_names: JSON.stringify([String(formData.nursing_staff_names || "").trim()].filter(Boolean)),
      assistant_surgeon_ids: JSON.stringify([parseInt(formData.assistant_surgeon_ids || 0, 10)]),
      nursing_staff_ids: JSON.stringify([parseInt(formData.nursing_staff_ids || 0, 10)]),
      preoperative_tests_required: formData.preoperative_tests_required ? 1 : 0,
      special_equipment_needed: formData.special_equipment_needed ? 1 : 0,
      blood_products_required: formData.blood_products_required ? 1 : 0,
      fasting_instructions_given: formData.fasting_instructions_given ? 1 : 0,
      preoperative_medications_provided: formData.preoperative_medications_provided ? 1 : 0,
      consent_forms_completed: formData.consent_forms_completed ? 1 : 0,
      is_major_ot: formData.is_major_ot ? 1 : 0,
    };

    try {
      await axios.post(`${API_BASE_URL}/update_ot_booking_by_uhid/${uhid_no}`, formatted, { withCredentials: true });
      showNotification('success', `OT Booking for ${formData.patient_name} updated successfully!`);
      setTimeout(() => navigate('/home', { state: { activeMenu: 'OT Booking' } }), 1000);
    } catch (error) {
      console.error('Update error:', error);
      const msg = error.response?.data?.error || 'Error in updating OT Booking!';
      showNotification('error', msg);
    }
  };

  useEffect(() => {
    axios.get(`${API_BASE_URL}/get_all_patients`, { withCredentials: true })
      .then(response => {
        setPatients(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching patients:', error);
        showNotification('error', 'Failed to fetch patient list');
        setLoading(false);
      });
  }, [showNotification]);
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'OT Booking' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              OT Booking Slip
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Patient Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="IP No"
                  name="ip_no"
                  type="number"
                  value={formData.ip_no}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.ip_no}
                  helperText={errors.ip_no}
                />
              </Grid>
              <Grid item xs={12} sm={6}>



                <Autocomplete
                  options={patients}
                  getOptionLabel={(option) => option.name || ""}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.name} – UHID: {option.uhid_no}
                    </li>
                  )}
                  value={patients.find(patient => patient.name === formData.patient_name) || null}
                  onChange={(_, newValue) => {
                    setSelectedPatient(newValue);
                    setFormData(prev => ({
                      ...prev,
                      patient_name: newValue?.name || "",
                      uhid_no: newValue?.uhid_no || ""
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Patient Name"
                      fullWidth
                      error={!!errors.uhid_no}
                      helperText={errors.uhid_no}
                      onBlur={() => {
                        if (!formData.uhid_no) setErrors(prev => ({ ...prev, uhid_no: "Patient is required" }));
                      }}
                    />
                  )}
                />
              </Grid>


              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date & Time of Booking (Optional)"
                  name="booking_datetime"
                  type="DateTime-local"
                  value={formData.booking_datetime}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="UHID No"
                  name="uhid_no"
                  value={formData.uhid_no}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                  onBlur={handleBlur}
                  error={!!errors.uhid_no}
                  helperText={errors.uhid_no}
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.uhid_no} value={patient.uhid_no}>
                      {patient.uhid_no}
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={allergiesOptions}
                  value={formData.allergies_medical_alerts || []}
                  onChange={(event, newValue) => {
                    handleChange({ target: { name: 'allergies_medical_alerts', value: newValue } });
                    setAllergiesError(false);  // clear error on change
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Any Allergies or Medical Alerts"
                      placeholder="Select or type allergies"
                      fullWidth

                      error={allergiesError}
                      helperText={allergiesError ? "This field is required." : ""}
                      inputRef={allergyInputRef}  // your existing ref for current typed allergy
                    />
                  )}
                />


              </Grid>

              {/* Surgery Details with Auto-fill */}
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!errors.type_of_surgery}>
                  <InputLabel id="type-of-surgery-label">Type of Surgery/Procedure</InputLabel>
                  <Select
                    labelId="type-of-surgery-label"
                    id="type-of-surgery"
                    value={formData.type_of_surgery || ''}
                    label="Type of Surgery/Procedure"
                    onChange={(event) =>
                      handleChange({
                        target: { name: 'type_of_surgery', value: event.target.value },
                      })
                    }
                    onBlur={handleBlur}
                  >
                    {surgeryData.map((surgery, index) => (
                      <MenuItem key={index} value={surgery.diagnosisDescription}>
                        {surgery.diagnosisDescription}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    disabled
                    fullWidth
                    variant="standard"
                    value=""
                    helperText="Select surgery to auto-fill codes"
                    InputProps={{ disableUnderline: true }}
                  />
                  {errors.type_of_surgery && <FormHelperText>{errors.type_of_surgery}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Surgical Code (ICD-10)"
                  name="surgical_code"
                  value={formData.surgical_code}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled when surgery is selected"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Procedure Code (CPT)"
                  name="procedure_code"
                  value={formData.procedure_code}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled when surgery is selected"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Procedure Description"
                  name="type_of_procedure"
                  value={formData.type_of_procedure}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled when surgery is selected"
                />
              </Grid>
              <Grid item xs={12} >
                <TextField
                  label="Date & Time of Surgery"
                  name="surgery_datetime"
                  InputLabelProps={{ shrink: true }}
                  type="DateTime-local" inputProps={{
                    min: new Date().toISOString().slice(0, 16), // Sets the minimum date and time to the current date and time
                  }}
                  value={formData.surgery_datetime}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.surgery_datetime}
                  helperText={errors.surgery_datetime}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Diagnosis/Reason for Surgery"
                  name="diagnosis_reason"
                  value={formData.diagnosis_reason}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.diagnosis_reason}
                  helperText={errors.diagnosis_reason}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Expected Duration (minutes)"
                  name="expected_duration"
                  type="number"
                  value={formData.expected_duration}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.expected_duration}
                  helperText={errors.expected_duration}
                />
              </Grid>

              {/* Preferred Theatre & Priority */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Preferred Operating Theatre"
                  name="preferred_operating_theatre"
                  value={formData.preferred_operating_theatre || ''}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  onBlur={handleBlur}
                  error={!!errors.preferred_operating_theatre}
                  helperText={errors.preferred_operating_theatre}
                >
                  {theatreOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.priority_status}>
                  <InputLabel>Priority Status</InputLabel>
                  <Select
                    label="Priority Status"
                    name="priority_status"
                    value={formData.priority_status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="Emergency">Emergency</MenuItem>
                    <MenuItem value="Urgent">Urgent</MenuItem>
                    <MenuItem value="Elective">Elective</MenuItem>
                  </Select>
                  {errors.priority_status && <FormHelperText>{errors.priority_status}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Surgeon & Staff Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Primary Surgeon Name"
                  name="primary_surgeon_name"
                  value={formData.primary_surgeon_name}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.primary_surgeon_name}
                  helperText={errors.primary_surgeon_name}
                >
                  {surgeonList.map((s) => (
                    <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Primary Surgeon ID"
                  name="primary_surgeon_id"
                  type="number"
                  value={formData.primary_surgeon_id}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Assistant Surgeon Name(s)"
                  name="assistant_surgeon_names"
                  value={formData.assistant_surgeon_names}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.assistant_surgeon_names}
                  helperText={errors.assistant_surgeon_names}
                >
                  {assistantSurgeonList.map((a) => (
                    <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Assistant Surgeon ID(s)"
                  name="assistant_surgeon_ids"
                  type="number"
                  value={formData.assistant_surgeon_ids}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Anesthesiologist Name"
                  name="anesthesiologist_name"
                  value={formData.anesthesiologist_name}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.anesthesiologist_name}
                  helperText={errors.anesthesiologist_name}
                >
                  {anesthesiologistList.map((a) => (
                    <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Anesthesiologist ID"
                  name="anesthesiologist_id"
                  type="number"
                  value={formData.anesthesiologist_id}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Nursing Staff Name"
                  name="nursing_staff_names"
                  value={formData.nursing_staff_names}
                  onChange={handleChange}
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.nursing_staff_names}
                  helperText={errors.nursing_staff_names}
                >
                  {nursingStaffList.map((n) => (
                    <MenuItem key={n.id} value={n.name}>{n.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nursing Staff ID(s)"
                  name="nursing_staff_ids"
                  type="number"
                  value={formData.nursing_staff_ids}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* Preoperative and Consent Information */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.preoperative_tests_required} onChange={handleChange} name="preoperative_tests_required" />}
                  label="Preoperative Tests/Reports Required"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.special_equipment_needed} onChange={handleChange} name="special_equipment_needed" />}
                  label="Special Equipment Needed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.blood_products_required} onChange={handleChange} name="blood_products_required" />}
                  label="Blood Products Required"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.fasting_instructions_given} onChange={handleChange} name="fasting_instructions_given" />}
                  label="Fasting Instructions Given"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.preoperative_medications_provided} onChange={handleChange} name="preoperative_medications_provided" />}
                  label="Preoperative Medications Provided"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.consent_forms_completed} onChange={handleChange} name="consent_forms_completed" />}
                  label="Consent Forms Completed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.is_major_ot} onChange={handleChange} name="is_major_ot" />}
                  label="Major OT / Minor OT"
                />
              </Grid>

              {/* Insurance and Payment */}
              <Grid item xs={12}>
                <Autocomplete
                  options={insuranceOptions}
                  value={formData.insurance_details || ''}
                  onChange={(event, newValue) => {
                    // When user selects an option from dropdown or clears it
                    handleChange({ target: { name: 'insurance_details', value: newValue || '' } });
                  }}
                  onInputChange={(event, newInputValue) => {
                    // When user types or edits input directly
                    handleChange({ target: { name: 'insurance_details', value: newInputValue || '' } });
                  }}
                  freeSolo // allow custom input, not limited to options
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Insurance Details"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.payment_method}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    label="Payment Method"
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="Cash" disabled={!!formData.insurance_details}>Cash</MenuItem>
                    <MenuItem value="Insurance">Insurance</MenuItem>
                    <MenuItem value="Online" disabled={!!formData.insurance_details}>Online</MenuItem>
                  </Select>
                  {errors.payment_method && <FormHelperText>{errors.payment_method}</FormHelperText>}
                </FormControl>
              </Grid>

              {formData.payment_method === "Online" && (
                <Box display="flex" flexDirection="column" sx={{ mt: '14px', ml: 2 }}>
                  <FormLabel sx={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', mb: 0 }}>
                    Select Online Payment Method
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.online_payment_option}
                    onChange={(e) =>
                      setFormData({ ...formData, online_payment_option: e.target.value, card_type: "" })
                    }
                  >
                    <FormControlLabel value="upi" control={<Radio />} label="UPI" />
                    <FormControlLabel value="card" control={<Radio />} label="Card" />
                    <FormControlLabel value="netbanking" control={<Radio />} label="Net Banking" />
                  </RadioGroup>

                  {/* Show Card Type only if Card is selected */}
                  {formData.online_payment_option === "card" && (
                    <FormControl sx={{ mt: 2 }}>
                      <FormLabel>Card Type</FormLabel>
                      <RadioGroup
                        row
                        value={formData.card_type}
                        onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
                      >
                        <FormControlLabel value="Debit" control={<Radio />} label="Debit Card" />
                        <FormControlLabel value="Credit" control={<Radio />} label="Credit Card" />
                      </RadioGroup>
                    </FormControl>
                  )}
                </Box>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Patient Room No / Ward"
                  name="room_no"
                  value={formData.room_no || ''}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  onBlur={handleBlur}
                  error={!!errors.room_no}
                  helperText={errors.room_no}
                >
                  {roomNumbers.map((room) => (
                    <MenuItem key={room} value={room}>
                      {room}
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>
              <Grid item xs={12} >
                <TextField
                  name='actual_start_time'
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                  type='datetime-local'
                  onChange={handleChange}
                  value={formData.actual_start_time}
                  InputLabelProps={{ shrink: true }}
                  label="Actual Start Time"
                  required
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.actual_start_time}
                  helperText={errors.actual_start_time}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Operation Status"
                  name="operation_status"
                  value={formData.operation_status}
                  onChange={handleChange}
                  select
                  fullWidth
                  required
                  onBlur={handleBlur}
                  error={!!errors.operation_status}
                  helperText={errors.operation_status}
                >
                  <MenuItem value="Scheduled">Scheduled</MenuItem>

                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Postponed">Postponed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ marginTop: 3 }}
                    fullWidth
                    disabled={formData.operation_status === "Completed"}
                  >
                    {formData.operation_status === "Completed" ? "Operation Completed - Cannot Update" : "Update OT Booking"}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default EditOtBooking;

